import React, { useState, useEffect, useCallback, forwardRef, lazy, Suspense } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, CircularProgress, Typography, IconButton, Button, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UniversalInventory from '../../components/UniversalInventory';
import RichText from '../../components/RichText';
import { v3Theme } from '../theme';
import { useTranslation } from 'react-i18next';
import useAuth from '../../hooks/useAuth';
import { fetchWarehouseData, fetchDepositItems, depositItemsToWarehouse, fetchWithdrawItems, withdrawItemsFromWarehouse } from '../../services/api';

// Lazy load heavy dialog components
const UniversalTransferDialog = lazy(() => import('./UniversalTransferDialog'));
const DepositSuccessDialog = lazy(() => import('./DepositSuccessDialog'));
const WithdrawSuccessDialog = lazy(() => import('./WithdrawSuccessDialog'));

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Constants for deposit cost calculation
const DEPOSIT_BASE_COST = 2;
const BULK_QUANTITY_THRESHOLD = 100;
const BULK_PRICE_DIVISOR = 50;

const WarehouseDialog = ({ open, onClose }) => {
    const { t, i18n } = useTranslation();
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [warehouseData, setWarehouseData] = useState(null);
    const [error, setError] = useState(null);
    const [depositDialogOpen, setDepositDialogOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [userInventory, setUserInventory] = useState(null);
    const [userFilters, setUserFilters] = useState(null);
    const [userGold, setUserGold] = useState(0);
    const [warehouseCurrentSize, setWarehouseCurrentSize] = useState(0);
    const [warehouseMaxSize, setWarehouseMaxSize] = useState(0);
    const [depositLoading, setDepositLoading] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [successDialogData, setSuccessDialogData] = useState({
        message: '',
        depositedItems: [],
        totalCost: 0
    });
    const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const [withdrawSuccessDialogOpen, setWithdrawSuccessDialogOpen] = useState(false);
    const [withdrawSuccessDialogData, setWithdrawSuccessDialogData] = useState({
        message: '',
        withdrawnItems: []
    });
    const [warehouseInventory, setWarehouseInventory] = useState(null);
    const [warehouseFilters, setWarehouseFilters] = useState(null);
    const [userStockSize, setUserStockSize] = useState(0);
    const [userMaxStockSize, setUserMaxStockSize] = useState(0);
    
    // Number formatting function
    const formatNumber = (num) => {
        return new Intl.NumberFormat(i18n.language).format(num);
    };

    const loadWarehouseData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchWarehouseData(token);
            setWarehouseData(data);
        } catch (err) {
            console.error('Error fetching warehouse data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (open && token) {
            loadWarehouseData();
        }
    }, [open, token, loadWarehouseData]);
    
    // Handle deposit button click
    const handleDeposit = async (location) => {
        setSelectedLocation(location);
        setDepositLoading(true);
        
        try {
            // Fetch available items for deposit from server
            const depositData = await fetchDepositItems(token);
            
            // Map server response to format expected by UniversalTransferDialog
            const mappedItems = depositData.items.map(item => ({
                id: item.id,
                name: item.name,
                icon: item.icon,
                quantity: item.quantity,
                weight: item.weight,
                category: item.category,
                cost: item.cost
            }));
            
            setUserInventory(mappedItems);
            setUserFilters(depositData.filters || null);
            setUserGold(depositData.userGold);
            // Set warehouse size info
            if (depositData.warehouseInfo) {
                setWarehouseCurrentSize(depositData.warehouseInfo.currentSize || 0);
                setWarehouseMaxSize(depositData.warehouseInfo.maxSize || 0);
            }
            setDepositDialogOpen(true);
        } catch (error) {
            console.error('Error fetching deposit items:', error);
            // Show error alert
            alert(t('widgets.inventory.errorLoadingDepositItems') || 'Failed to load available items for deposit');
        } finally {
            setDepositLoading(false);
        }
    };
    
    // Handle withdraw button click
    const handleWithdraw = async (location) => {
        setSelectedLocation(location);
        setWithdrawLoading(true);
        
        try {
            // Fetch available items for withdrawal from server
            const withdrawData = await fetchWithdrawItems(token);
            
            // Map server response to format expected by UniversalTransferDialog
            const mappedItems = withdrawData.items.map(item => ({
                id: item.id,
                name: item.name,
                icon: item.icon,
                quantity: item.quantity,
                weight: item.weight,
                category: item.category
            }));
            
            setWarehouseInventory(mappedItems);
            setWarehouseFilters(withdrawData.filters || null);
            // Set user stock info
            if (withdrawData.userStock) {
                setUserStockSize(withdrawData.userStock.currentSize || 0);
                setUserMaxStockSize(withdrawData.userStock.maxSize || 0);
            }
            setWithdrawDialogOpen(true);
        } catch (error) {
            console.error('Error fetching withdraw items:', error);
            // Show error alert
            alert(t('widgets.inventory.errorLoadingWithdrawItems') || 'Failed to load available items for withdrawal');
        } finally {
            setWithdrawLoading(false);
        }
    };
    
    // Calculate deposit cost using server-provided costs - memoized pure function
    const calculateDepositCost = useCallback((item, quantity) => {
        // Use the cost provided by server for each item (calculated for qty=1)
        const baseCost = item.cost || DEPOSIT_BASE_COST;
        
        // Apply special rule for bulk quantities
        // Server uses: price * Round(qty/50.0) when qty > 100
        if (quantity > BULK_QUANTITY_THRESHOLD) {
            return baseCost * Math.round(quantity / BULK_PRICE_DIVISOR);
        }
        
        // For quantities <= 100, simple multiplication
        return baseCost * quantity;
    }, []); // No dependencies, pure function
    
    // Handle deposit confirmation
    const handleDepositConfirm = useCallback(async (selectedItems, totalCost) => {
        setDepositLoading(true);
        
        try {
            // Send deposit request to server
            const response = await depositItemsToWarehouse(token, selectedItems);
            
            if (response.success) {
                // Close transfer dialog
                setDepositDialogOpen(false);
                
                // Update warehouse data if provided
                if (response.warehouseUpdate) {
                    setWarehouseData(prevData => {
                        if (!prevData) return prevData;
                        
                        // Find and update the specific location
                        const updatedLocations = prevData.locations.map(loc => {
                            if (loc.location === response.warehouseUpdate.location) {
                                return response.warehouseUpdate;
                            }
                            return loc;
                        });
                        
                        // If location doesn't exist, add it
                        if (!updatedLocations.find(loc => loc.location === response.warehouseUpdate.location)) {
                            updatedLocations.push(response.warehouseUpdate);
                            // Re-sort: manageable first, then by location
                            updatedLocations.sort((a, b) => {
                                if (a.canManage !== b.canManage) {
                                    return a.canManage ? -1 : 1;
                                }
                                return a.location.localeCompare(b.location);
                            });
                        }
                        
                        // Recalculate totals
                        let totalSize = 0;
                        let totalMaxSize = 0;
                        updatedLocations.forEach(loc => {
                            totalSize += loc.currentSize;
                            totalMaxSize += loc.maxSize;
                        });
                        
                        return {
                            ...prevData,
                            locations: updatedLocations,
                            totalSize: totalSize,
                            totalMaxSize: totalMaxSize
                        };
                    });
                }
                
                // Note: Inventory update will be handled via SSE channel automatically
                
                // Prepare success dialog data
                setSuccessDialogData({
                    message: response.message || t('widgets.inventory.itemsDepositedSuccessfully'),
                    depositedItems: response.depositedItems || [],
                    totalCost: response.cost || 0
                });
                
                // Show success dialog
                setSuccessDialogOpen(true);
            } else {
                // Show error message from server
                alert(response.message || t('widgets.inventory.depositFailed'));
            }
        } catch (error) {
            console.error('Error depositing items:', error);
            alert(t('widgets.inventory.depositFailed') || 'Failed to deposit items. Please try again.');
        } finally {
            setDepositLoading(false);
        }
    }, [token, t]);
    
    // Handle withdraw confirmation
    const handleWithdrawConfirm = useCallback(async (selectedItems) => {
        setWithdrawLoading(true);
        
        try {
            // Send withdraw request to server
            const response = await withdrawItemsFromWarehouse(token, selectedItems);
            
            if (response.success) {
                // Close transfer dialog
                setWithdrawDialogOpen(false);
                
                // Update warehouse data if provided
                if (response.warehouseUpdate) {
                    setWarehouseData(prevData => {
                        if (!prevData) return prevData;
                        
                        // Find and update the specific location
                        const updatedLocations = prevData.locations.map(loc => {
                            if (loc.location === response.warehouseUpdate.location) {
                                return response.warehouseUpdate;
                            }
                            return loc;
                        });
                        
                        // Recalculate totals
                        let totalSize = 0;
                        let totalMaxSize = 0;
                        updatedLocations.forEach(loc => {
                            totalSize += loc.currentSize;
                            totalMaxSize += loc.maxSize;
                        });
                        
                        return {
                            ...prevData,
                            locations: updatedLocations,
                            totalSize: totalSize,
                            totalMaxSize: totalMaxSize
                        };
                    });
                }
                
                // Note: Inventory update will be handled via SSE channel automatically
                
                // Prepare success dialog data
                setWithdrawSuccessDialogData({
                    message: response.message || t('widgets.inventory.itemsWithdrawnSuccessfully'),
                    withdrawnItems: response.withdrawnItems || []
                });
                
                // Show success dialog
                setWithdrawSuccessDialogOpen(true);
            } else {
                // Show error message from server
                alert(response.message || t('widgets.inventory.withdrawFailed'));
            }
        } catch (error) {
            console.error('Error withdrawing items:', error);
            alert(t('widgets.inventory.withdrawFailed') || 'Failed to withdraw items. Please try again.');
        } finally {
            setWithdrawLoading(false);
        }
    }, [token, t]);

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose}
                TransitionComponent={Transition}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        minHeight: '60vh'
                    }
                }}
            >
            <DialogTitle sx={{ 
                m: 0, 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                borderBottom: `1px solid ${v3Theme.border.light}`
            }}>
                <span style={{ color: v3Theme.main, fontWeight: 600, fontSize: '1.25rem' }}>
                    {t('widgets.inventory.warehouse')}
                </span>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: v3Theme.text.secondary,
                        '&:hover': {
                            backgroundColor: v3Theme.background.hover
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                {loading ? (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: '400px' 
                    }}>
                        <CircularProgress sx={{ color: v3Theme.main }} />
                    </Box>
                ) : error ? (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: '400px',
                        p: 3
                    }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : warehouseData && warehouseData.locations ? (
                    <Box>
                        {warehouseData.locations.length === 0 ? (
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                minHeight: '300px',
                                p: 3
                            }}>
                                <Typography sx={{ color: v3Theme.text.secondary }}>
                                    {t('widgets.inventory.noItemsFound')}
                                </Typography>
                            </Box>
                        ) : (
                            warehouseData.locations.map((location, index) => (
                                <Box key={location.location} sx={{ mb: index < warehouseData.locations.length - 1 ? 3 : 0 }}>
                                    {/* Show management buttons if user can manage this warehouse */}
                                    {location.canManage && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            gap: 2, 
                                            mb: 2,
                                            px: 2,
                                            pt: 2
                                        }}>
                                            <Button
                                                variant="contained"
                                                onClick={() => handleDeposit(location)}
                                            disabled={depositLoading}
                                                sx={{
                                                    flex: 1,
                                                    backgroundColor: v3Theme.button.primary,
                                                    color: 'white',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        backgroundColor: v3Theme.button.primaryHover,
                                                    }
                                                }}
                                            >
                                                {t('widgets.inventory.deposit')}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={() => handleWithdraw(location)}
                                                disabled={!location.items || location.items.length === 0 || withdrawLoading}
                                                sx={{
                                                    flex: 1,
                                                    backgroundColor: v3Theme.button.secondary,
                                                    color: 'white',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        backgroundColor: v3Theme.button.secondaryHover,
                                                    },
                                                    '&:disabled': {
                                                        backgroundColor: v3Theme.button.disabled,
                                                        color: v3Theme.text.disabled,
                                                    }
                                                }}
                                            >
                                                {t('widgets.inventory.withdraw')}
                                            </Button>
                                        </Box>
                                    )}
                                    <UniversalInventory
                                        title={
                                            <RichText 
                                                content={location.title
                                                    .replace(location.currentSize.toString(), formatNumber(location.currentSize))
                                                    .replace(location.maxSize.toString(), formatNumber(location.maxSize))}
                                            />
                                        }
                                        inventory={location.items.map(item => ({
                                            name: item.name,
                                            icon: item.icon,
                                            qty: item.quantity.toString(),
                                            code: item.code,
                                            category: item.category,
                                            actions: item.actions || []
                                        }))}
                                        filters={location.filters || ['All']}
                                    />
                                </Box>
                            ))
                        )}
                    </Box>
                ) : null}
            </DialogContent>
            </Dialog>
            
            {/* Deposit Transfer Dialog */}
            {depositDialogOpen && selectedLocation && (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: v3Theme.main }} /></Box>}>
                    <UniversalTransferDialog
                        open={depositDialogOpen}
                        onClose={() => setDepositDialogOpen(false)}
                        title={`${t('widgets.inventory.deposit')} ${t('widgets.inventory.toWarehouse')} [${selectedLocation.coordinates}]`}
                        sourceItems={userInventory || []}
                        filters={userFilters}
                        onConfirm={handleDepositConfirm}
                        calculateCost={calculateDepositCost}
                        userGold={userGold}
                        warehouseCurrentSize={warehouseCurrentSize}
                        warehouseMaxSize={warehouseMaxSize}
                        confirmText={t('widgets.inventory.confirmDeposit')}
                        costLabel={t('widgets.inventory.depositCost')}
                    />
                </Suspense>
            )}
            
            {/* Deposit Success Dialog */}
            {successDialogOpen && (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: v3Theme.main }} /></Box>}>
                    <DepositSuccessDialog
                        open={successDialogOpen}
                        onClose={() => setSuccessDialogOpen(false)}
                        message={successDialogData.message}
                        depositedItems={successDialogData.depositedItems}
                        totalCost={successDialogData.totalCost}
                    />
                </Suspense>
            )}
            
            {/* Withdraw Transfer Dialog */}
            {withdrawDialogOpen && selectedLocation && (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: v3Theme.main }} /></Box>}>
                    <UniversalTransferDialog
                        open={withdrawDialogOpen}
                        onClose={() => setWithdrawDialogOpen(false)}
                        title={`${t('widgets.inventory.withdraw')} ${t('widgets.inventory.fromWarehouse')} [${selectedLocation.coordinates}]`}
                        sourceItems={warehouseInventory || []}
                        filters={warehouseFilters}
                        onConfirm={handleWithdrawConfirm}
                        calculateCost={null} // No cost for withdrawal
                        userGold={0} // Not needed for withdrawal
                        warehouseCurrentSize={userStockSize}
                        warehouseMaxSize={userMaxStockSize}
                        confirmText={t('widgets.inventory.confirmWithdraw')}
                        costLabel="" // No cost label for withdrawal
                    />
                </Suspense>
            )}
            
            {/* Withdraw Success Dialog */}
            {withdrawSuccessDialogOpen && (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: v3Theme.main }} /></Box>}>
                    <WithdrawSuccessDialog
                        open={withdrawSuccessDialogOpen}
                        onClose={() => setWithdrawSuccessDialogOpen(false)}
                        message={withdrawSuccessDialogData.message}
                        withdrawnItems={withdrawSuccessDialogData.withdrawnItems}
                    />
                </Suspense>
            )}
        </>
    );
};

export default WarehouseDialog;